// On-screen touch controls (§8). Redesigned for mobile/tablet to behave like a
// keydown-based scheme rather than a fidgety drag cursor: two on-screen buttons
// (◀ / ▶) steer left/right while held, and TAP anywhere else jumps. Holding a
// steer button gives full deflection (raw ±1), exactly like the keyboard's A/D,
// so the feel is consistent across input methods.
export class TouchControls {
  steer = 0;
  jumpEdge = false;
  pauseEdge = false;
  active = false;

  private root: HTMLElement;
  private leftBtn: HTMLElement;
  private rightBtn: HTMLElement;

  // Pointers currently holding each steer button. A button stays "pressed"
  // until every finger on it lifts, so multi-touch can't leave it stuck.
  private leftPointers = new Set<number>();
  private rightPointers = new Set<number>();

  // Pending jump taps keyed by pointer id (start position + time), so a quick
  // tap (jump) can be told apart from a finger that just rests on the screen.
  private taps = new Map<number, { x: number; y: number; t: number }>();

  constructor(parent: HTMLElement) {
    this.root = document.createElement('div');
    this.root.className = 'touch-surface';
    this.root.innerHTML = `
      <button type="button" class="touch-btn touch-btn-left" aria-label="Steer left">◀</button>
      <button type="button" class="touch-btn touch-btn-right" aria-label="Steer right">▶</button>
      <div class="touch-hint">◀ ▶ steer · tap to jump</div>
    `;
    parent.appendChild(this.root);
    this.leftBtn = this.root.querySelector('.touch-btn-left') as HTMLElement;
    this.rightBtn = this.root.querySelector('.touch-btn-right') as HTMLElement;

    this.bindButton(this.leftBtn, this.leftPointers);
    this.bindButton(this.rightBtn, this.rightPointers);

    // Tap anywhere on the surface (steer buttons stopPropagation, so a press on
    // them never counts as a jump tap). Release is tracked on window so a tap
    // that drifts off-surface still resolves.
    this.root.addEventListener('pointerdown', this.onSurfaceDown);
    window.addEventListener('pointerup', this.onSurfaceUp);
    window.addEventListener('pointercancel', this.onSurfaceUp);
  }

  /** Wire a steer button: held = full deflection in its direction. */
  private bindButton(btn: HTMLElement, pointers: Set<number>): void {
    btn.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.active = true;
      pointers.add(e.pointerId);
      btn.classList.add('pressed');
      this.updateSteer();
    });
    // Release on window so a finger sliding off the button still lets go.
    const release = (e: PointerEvent) => {
      if (!pointers.delete(e.pointerId)) return;
      if (pointers.size === 0) btn.classList.remove('pressed');
      this.updateSteer();
    };
    window.addEventListener('pointerup', release);
    window.addEventListener('pointercancel', release);
  }

  private updateSteer(): void {
    this.steer = (this.rightPointers.size ? 1 : 0) - (this.leftPointers.size ? 1 : 0);
  }

  private onSurfaceDown = (e: PointerEvent) => {
    e.preventDefault();
    this.active = true;
    this.taps.set(e.pointerId, { x: e.clientX, y: e.clientY, t: performance.now() });
  };

  private onSurfaceUp = (e: PointerEvent) => {
    const tap = this.taps.get(e.pointerId);
    if (!tap) return;
    this.taps.delete(e.pointerId);
    // A quick touch that barely moved is a tap -> jump.
    const moved = Math.hypot(e.clientX - tap.x, e.clientY - tap.y);
    if (performance.now() - tap.t < 250 && moved < 18) this.jumpEdge = true;
  };

  /** Pause button lives above the surface; tapping it must not also jump. */
  setPauseButton(btn: HTMLElement): void {
    btn.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.pauseEdge = true;
    });
  }

  setVisible(v: boolean): void {
    this.root.style.display = v ? 'block' : 'none';
    if (!v) this.reset();
  }

  private reset(): void {
    this.leftPointers.clear();
    this.rightPointers.clear();
    this.taps.clear();
    this.steer = 0;
    this.leftBtn.classList.remove('pressed');
    this.rightBtn.classList.remove('pressed');
  }
}
